import {
  COMMUNICATION_SETTING,
  PHOTO_SETTING,
  PRINT_SETTING,
  resolveStationSetting,
  STATION_MODE_SETTING,
} from './settings';

/**
 * Check if communication mode is WebSocket
 */
export const isWebSocketCommunication = (search?: string): boolean =>
  resolveStationSetting(COMMUNICATION_SETTING, search) === 'websockets';

/**
 * Check if station mode is enabled
 */
export const isStationModeEnabled = (search?: string): boolean =>
  resolveStationSetting(STATION_MODE_SETTING, search);

/** Album cover printing, switched off with ?print=false */
export const isStationPrintEnabled = (search?: string): boolean =>
  resolveStationSetting(PRINT_SETTING, search);

/** Camera capture during generation, switched off with ?photo=false */
export const isStationPhotoEnabled = (search?: string): boolean =>
  resolveStationSetting(PHOTO_SETTING, search);

/**
 * Check if WebSocket should be used
 * Returns true if both conditions are met:
 * 1. communication=websockets in URL
 * 2. Station mode is enabled
 */
export const shouldUseWebSocket = (): boolean =>
  isWebSocketCommunication() && isStationModeEnabled();

/**
 * Get WebSocket URL from environment or use default
 */
export const getWebSocketUrl = (): string => {
  // Check environment variables first
  if (import.meta.env.VITE_STATION_WEBSOCKET_URL) {
    return import.meta.env.VITE_STATION_WEBSOCKET_URL;
  }

  // Default to localhost in development
  if (import.meta.env.DEV) {
    return 'http://localhost:3000';
  }

  // In production, use current origin
  return window.location.origin.replace(/:\d+/, ':3000');
};

/**
 * Clear communication mode from localStorage
 * Useful for testing or switching back to direct communication
 */
export const clearCommunicationMode = (): void => {
  try {
    localStorage.removeItem(COMMUNICATION_SETTING.storageKey);
  } catch {
    // Ignore localStorage errors
  }
};

/**
 * Get authenticated user ID from OIDC user object
 * This extracts the user ID from the authenticated user's profile
 *
 * @param user - The OIDC user object from useAuth()
 * @returns The user ID (profile.sub) or null if not authenticated
 */
export const getAuthenticatedUserId = (user: any): string | null => {
  try {
    // Extract user ID from OIDC user profile
    const userId = user?.profile?.sub;
    return userId || null;
  } catch {
    return null;
  }
};
